import { FC } from "react";
import { NextArrowIcon } from "src/assets/Icons";
import SkeletonBox from "src/components/SkeletonBox/SkeletonBox";
import { pointsExchangeGridDataMap, pointsExchangeHeaderDataMap } from "../constants";
import { STATUS_MAP } from "src/Modules/UserHistory/constants/OrdersList";
import { transformRecord } from "src/utility";
import { useTranslation } from "react-i18next";
import TermsAndConditions from "../components/TermsAndConditions/TermsAndConditions";
import Tag from "src/components/Tag/Tag";
import PointsExchangeActionCard from "../components/PointsExchangeActionCard/PointsExchangeActionCard";

const Skeleton = () => (
  <div className="flex w-full flex-col gap-3 pb-5 md:flex-row md:items-start md:p-5">
    <div className="flex flex-5">
      <div className="flex w-full flex-col gap-4 rounded-sm bg-background-default p-5 outline outline-border-default">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <SkeletonBox className="size-20 shrink-0 rounded-md" />
            <SkeletonBox className="h-7 w-48 max-w-full" />
          </div>
          <SkeletonBox className="h-7 w-24 shrink-0 rounded-full" />
        </div>
        <SkeletonBox className="h-4 w-32" />
        <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center">
          {[0, 1].map((i) => (
            <div key={i} className="flex flex-1 flex-col gap-1 rounded-md bg-background-minimal px-4 py-3">
              <SkeletonBox className="h-4 w-24" />
              <SkeletonBox className="h-6 w-20" />
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-1 rounded-md border border-dashed border-border-primary bg-background-primary-lighter px-4 py-3">
          <SkeletonBox className="h-4 w-28" />
          <SkeletonBox className="h-7 w-36" />
        </div>
        <div className="border-t border-dashed border-border-default pt-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-5 md:grid-cols-3">
            {[0, 1].map((i) => (
              <div key={i} className="flex flex-col gap-0.5">
                <SkeletonBox className="h-4 w-20" />
                <SkeletonBox className="h-5 w-24" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    <div className="flex flex-2">
      <div className="hidden w-full flex-col rounded-xs bg-background-default shadow-sm md:flex">
        <div className="flex items-center gap-3 border-b border-border-subtle px-5 py-4">
          <SkeletonBox className="h-10 w-10 shrink-0 rounded-full" />
          <SkeletonBox className="h-5 w-36" />
        </div>
        <div className="flex flex-col gap-2 px-5 py-4">
          <SkeletonBox className="h-3 w-full" />
          <SkeletonBox className="h-3 w-full" />
          <SkeletonBox className="h-3 w-5/6" />
          <SkeletonBox className="h-3 w-2/3" />
        </div>
      </div>
      <div className="flex w-full items-center gap-3 rounded-xl bg-background-default p-4 outline outline-border-subtle md:hidden">
        <SkeletonBox className="h-10 w-10 shrink-0 rounded-full" />
        <SkeletonBox className="h-5 flex-1" />
        <SkeletonBox className="h-5 w-5 shrink-0" />
      </div>
    </div>
  </div>
);

const PointsExchangeDetailsWeb: FC<any> = ({ orderItems, statusEnum, total, referenceNumber, orderedAt, isFetching }) => {
  const { t } = useTranslation();
  const statusEntry = STATUS_MAP[statusEnum as keyof typeof STATUS_MAP];
  if (isFetching) return <Skeleton />;

  const headerItems = transformRecord({ referenceNumber, orderedAt }, pointsExchangeHeaderDataMap);
  const [sourceItem, conversionItem, destinationItem] = transformRecord(
    {
      total,
      conversionRatio: { from: total, to: orderItems?.[0]?.points },
      destinationPoints: orderItems?.[0]?.points,
    },
    pointsExchangeGridDataMap,
  );

  return (
    <div className="flex w-full flex-col gap-3 pb-5 md:flex-row md:items-start md:p-5">
      <div className="flex flex-5">
        <div className="flex w-full flex-col gap-4 rounded-sm bg-background-default p-5 outline outline-border-default">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="size-20 shrink-0 overflow-hidden rounded-md outline outline-border-subtle">
                <img src={orderItems?.[0]?.imageUrl} alt={orderItems?.[0]?.name} className="h-full w-full object-cover" />
              </div>
              <div className="text-font-default text-xl font-bold">{orderItems?.[0]?.name}</div>
            </div>
            {statusEntry && (
              <Tag color={statusEntry.color} label={statusEntry.label} className="shrink-0 rounded-full px-4 py-1 text-sm outline outline-current" />
            )}
          </div>

          <div className="text-font-subtle text-sm font-semibold">{t("UserHistory.giftcard.exchangeInformation")}</div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center">
              <PointsExchangeActionCard
                className="flex-1"
                label={sourceItem?.label}
                value={sourceItem?.display}
                action={<span className="text-font-danger text-xs font-semibold">{t("UserHistory.orderDetails.points")}</span>}
              />
              <div className="flex size-9 shrink-0 items-center justify-center self-center rounded-full border border-border-subtle bg-background-default">
                <NextArrowIcon className="text-font-active h-4! w-4! rotate-90 md:rotate-0" />
              </div>
              <PointsExchangeActionCard className="flex-1" label={destinationItem?.label} value={destinationItem?.display} />
            </div>
            <PointsExchangeActionCard variant="highlight" label={conversionItem?.label} value={conversionItem?.display} />
          </div>

          <div className="border-t border-dashed border-border-default pt-4">
            <div className="grid grid-cols-2 gap-x-4 gap-y-5 md:grid-cols-3">
              {headerItems.map(({ key, label, display }) => (
                <div key={key} className="flex flex-col gap-0.5">
                  <div className="text-xs font-semibold uppercase tracking-wider text-font-subtle">{label}</div>
                  <div className="text-sm font-semibold text-font-default">{display}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-2">
        <TermsAndConditions termsAndCondition={orderItems?.[0]?.termsAndCondition} />
      </div>
    </div>
  );
};

export default PointsExchangeDetailsWeb;
